import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Input, Space } from 'antd'
import classNames from 'classnames'
import { useState } from 'react'

import cn from './ListInput.module.scss'

const PASTE_SEPARATOR = /[\r\n;]+/

interface Row {
  id: number
  value: string
}

interface ListState {
  rows: Row[]
  nextId: number
  source: string[]
  focusId: number | null
}

const createState = (values: string[], nextId: number): ListState => {
  const items = values.length ? values : ['']
  return {
    rows: items.map((value, index) => ({ id: nextId + index, value })),
    nextId: nextId + items.length,
    source: values,
    focusId: null,
  }
}

const hasSameValues = (rows: Row[], values: string[]) =>
  rows.length === values.length &&
  rows.every((row, index) => row.value === values[index])

interface ListInputProps {
  title?: string
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  addLabel?: string
  error?: string
  disabled?: boolean
  maxCount?: number
  maxLength?: number
  layout?: 'row' | 'stacked'
  isOrganisationSelected?: boolean
}

function ListInput({
  title,
  value,
  onChange,
  placeholder,
  addLabel = 'Добавить',
  error,
  disabled,
  maxCount,
  maxLength,
  layout = 'row',
  isOrganisationSelected = false,
}: ListInputProps) {
  const [state, setState] = useState(() => createState(value, 0))

  if (state.source !== value && !hasSameValues(state.rows, value)) {
    setState(createState(value, state.nextId))
  }

  const { rows, nextId, focusId } = state
  const canAdd =
    !disabled &&
    rows.every((row) => row.value.trim() !== '') &&
    (maxCount === undefined || rows.length < maxCount)

  const commit = (
    nextRows: Row[],
    nextNextId = nextId,
    nextFocusId: number | null = null
  ) => {
    const values = nextRows.map((row) => row.value)
    setState({
      rows: nextRows,
      nextId: nextNextId,
      source: values,
      focusId: nextFocusId,
    })
    onChange(values)
  }

  const insertAfter = (baseRows: Row[], index: number, values: string[]) => {
    const added = values.map((item, offset) => ({
      id: nextId + offset,
      value: item,
    }))
    commit(
      [...baseRows.slice(0, index + 1), ...added, ...baseRows.slice(index + 1)],
      nextId + added.length,
      added[added.length - 1].id
    )
  }

  const updateRow = (index: number, nextValue: string) =>
    commit(
      rows.map((row, i) => (i === index ? { ...row, value: nextValue } : row))
    )

  const removeRow = (index: number) =>
    rows.length === 1
      ? commit([{ ...rows[0], value: '' }])
      : commit(rows.filter((_, i) => i !== index))

  const handlePaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const parts = event.clipboardData
      .getData('text')
      .split(PASTE_SEPARATOR)
      .map((part) => part.trim())
      .filter((part) => part !== '')
    if (parts.length < 2) return
    event.preventDefault()
    if (rows[index].value.trim() !== '') {
      insertAfter(rows, index, parts)
      return
    }
    const [first, ...rest] = parts
    insertAfter(
      rows.map((row, i) => (i === index ? { ...row, value: first } : row)),
      index,
      rest
    )
  }

  const handleEnter = (index: number) => {
    if (canAdd) insertAfter(rows, index, [''])
  }

  return (
    <div
      className={classNames(
        title ? cn.listItem : cn.listBare,
        layout === 'stacked' && cn.stacked,
        isOrganisationSelected && cn.accentSelected
      )}
    >
      {title && <label>{title}</label>}
      <div className={cn.field}>
        {rows.map((row, index) => (
          <Space.Compact
            key={row.id}
            className={rows.length === 1 ? cn.rowWide : cn.row}
          >
            <Input
              value={row.value}
              onChange={(e) => updateRow(index, e.target.value)}
              onPaste={(e) => handlePaste(index, e)}
              onPressEnter={() => handleEnter(index)}
              placeholder={placeholder}
              maxLength={maxLength}
              status={error ? 'error' : undefined}
              disabled={disabled}
              autoFocus={row.id === focusId}
              suffix={
                <button
                  type="button"
                  className={cn.removeButton}
                  onClick={() => removeRow(index)}
                  disabled={disabled || (rows.length === 1 && row.value === '')}
                  aria-label="Удалить значение"
                >
                  <CloseOutlined />
                </button>
              }
            />
            {index === rows.length - 1 && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => insertAfter(rows, rows.length - 1, [''])}
                disabled={!canAdd}
                title={addLabel}
                aria-label={addLabel}
              />
            )}
          </Space.Compact>
        ))}
        {error && <span className={cn.error}>{error}</span>}
      </div>
    </div>
  )
}

export default ListInput
